import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloseLoopFilter, CloseLoopRequestFilter } from "../../../components/modals/close-loop-filter/types";
import { useFilterContext } from "../../../hooks/FilterContext";
import { useLazySearchCloseLoopApplicationsQuery } from "../../../services/headofficeadmin";
import { openConfirmExportModal } from "../components/ConfirmExportModal";
import { CloseLoopApplicationType } from "../types";
import useCloseLoopExport from "./useCloseLoopExport";

export type CloseLoopGridDataContextProps = {
    clear: () => Promise<void>;
    data: CloseLoopApplicationType[];
    excludeIds: string[];
    toggleExclude: (appId: string) => void;
    error: Error | null;
    isError: boolean;
    isClearing: boolean;
    isRowLoaded: (index: number) => boolean;
    sort: string;
    sortTable: (property: string) => void;
    loadMore: () => Promise<void>;
    totalCount: number;
    confirmExport: () => Promise<void>;
};

export type CloseLoopGridDataProviderProps = {
    children: React.ReactNode;
    limit?: number;
    initialSort?: string;
};

const CloseLoopGridDataContext = React.createContext<CloseLoopGridDataContextProps>({
    clear: () => Promise.resolve(),
    data: [],
    excludeIds: [],
    toggleExclude: () => { },
    error: null,
    isError: false,
    isClearing: false,
    isRowLoaded: (): boolean => false,
    sort: "",
    sortTable: () => { },
    loadMore: () => Promise.resolve(),
    totalCount: 0,
    confirmExport: () => Promise.resolve()
});

export const useCloseLoopGridData = () => React.useContext(CloseLoopGridDataContext);

export const CloseLoopGridDataProvider = ({
    children,
    limit = 100,
    initialSort = "lastDealDate+DESC"
}: CloseLoopGridDataProviderProps) => {
    const { filter, isReady } = useFilterContext();

    const requestFilter = useMemo((): CloseLoopRequestFilter => {
        const typedFilter = filter as CloseLoopFilter;
        // Replace Range filters with '...From' and '...To' and map providers to IDs
        const { applicationDate, statusDate, lastDealDate, providers, ...rest } = typedFilter;

        return {
            applicationDateFrom: applicationDate.from,
            applicationDateTo: applicationDate.to,
            statusDateFrom: statusDate.from,
            statusDateTo: statusDate.to,
            lastDealDateFrom: lastDealDate.from,
            lastDealDateTo: lastDealDate.to,
            providerIds: providers.map(obj => obj.value),
            ...rest
        };
    }, [filter]);

    const [sort, setSort] = useState<string>(() => {
        const storedSort = sessionStorage.getItem("sort.close-loop-dashboard");
        return storedSort ?? initialSort;
    });
    useEffect(() => {
        sessionStorage.setItem("sort.close-loop-dashboard", sort);
    }, [sort]);


    const [cacheKey, setCacheKey] = useState<number>(new Date().valueOf());
    const [isClearing, setIsClearing] = useState<boolean>(false);

    const [search, { data, error, isError, isFetching, isLoading }] = useLazySearchCloseLoopApplicationsQuery();

    const { pagination, results } = data || { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isNextPageLoading = isFetching && !isLoading;

    // Track Application IDs to be excluded from export
    // Note: Backend will filter out business sheets where all applications are excluded
    const [excludeIds, setExcludeIds] = useState<string[]>(() => {
        const stored = sessionStorage.getItem("exclude.close-loop-dashboard");
        return stored ? JSON.parse(stored) : [];
    });

    const toggleExclude = (appId: string) => {
        setExcludeIds((prevIds) => {
            if (prevIds.includes(appId)) {
                return prevIds.filter(id => id !== appId);
            } else {
                return [...prevIds, appId];
            }
        });
    };

    useEffect(() => {
        sessionStorage.setItem("exclude.close-loop-dashboard", JSON.stringify(excludeIds));
    }, [excludeIds]);

    const clear = useCallback(async (): Promise<any> => {
        if (isFetching || !isReady)
            return;

        setIsClearing(true);
        const newCacheKey = new Date().valueOf();

        await search({
            cacheKey: newCacheKey,
            page: 1,
            limit,
            sort,
            filter: requestFilter
        }).unwrap();

        setCacheKey(newCacheKey);
        setIsClearing(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, filter, isReady]);

    const isRowLoaded = (index: number): boolean =>
        !hasMore || index < results.length;

    const loadMore = (): Promise<void> => {
        if (isNextPageLoading || !isReady)
            return Promise.resolve();

        return search({
            cacheKey,
            sort,
            page: page + 1,
            limit,
            filter: requestFilter
        }).unwrap();
    };

    const sortTable = (property: string) => {
        let newDirection = "";
        let [sortProperty, direction] = sort.split("+");

        if (property === sortProperty && direction !== "DESC") {
            newDirection = "+DESC";
        }

        setSort(property + newDirection);
    };

    useEffect(() => {
        clear();
    }, [clear]);

    // TODO: Export permissions

    const { exportLetters } = useCloseLoopExport({
        isFetching,
        isError,
        filter: requestFilter,
        excludeIds
    });

    const confirmExport = () => {
        const excluded = (results as CloseLoopApplicationType[]).filter(app => excludeIds.includes(app.applicationId))
            .sort((a, b) => a.masterAccountName.localeCompare(b.masterAccountName));
        return openConfirmExportModal(totalCount, excluded).then(
            (markLoopClosed) => exportLetters(markLoopClosed)
                .then(() => {
                    if (markLoopClosed) {
                        setExcludeIds([]);
                        clear();
                    }
                }),
            () => { /* Export cancelled */ }
        ).catch((err) => console.error(err));
    };

    return (
        <CloseLoopGridDataContext.Provider value={{
            clear,
            data: (isClearing || isError) ? [] : results,
            excludeIds,
            toggleExclude,
            error,
            isError,
            isClearing,
            isRowLoaded,
            sort,
            sortTable,
            loadMore,
            totalCount: (isClearing || isError) ? 0 : totalCount,
            confirmExport
        }}>
            {children}
        </CloseLoopGridDataContext.Provider>
    );
};

export default CloseLoopGridDataContext;